import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { forwardRef, type ReactNode } from 'react'
import { IconTile } from 'shared/components/icon-tile'
import { CARD_FRAME_CLASSNAME, CARD_PADDING_CLASSNAME } from 'shared/constants/card-class-names'
import { twMerge } from 'tailwind-merge'

interface OfferConfigurationCardProps extends Omit<
  useRender.ComponentProps<'section'>,
  'children' | 'title' | 'content'
> {
  action?: ReactNode
  children?: ReactNode
  description?: ReactNode
  icon: ReactNode
  title?: ReactNode
  content?: ReactNode
}

const OfferConfigurationCard = forwardRef<HTMLElement, OfferConfigurationCardProps>(
  ({ action, children, className, description, icon, render, title, content, ...props }, ref) =>
    useRender({
      defaultTagName: 'section',
      ref,
      render,
      props: mergeProps<'section'>(
        {
          className: twMerge(CARD_FRAME_CLASSNAME, className),
          children: (
            <>
              <div
                className={twMerge(
                  CARD_PADDING_CLASSNAME,
                  'flex items-center justify-between gap-3',
                )}
              >
                <div className="flex w-full items-center gap-2">
                  <IconTile size="m" tone="gray">
                    {icon}
                  </IconTile>
                  <div className={'flex w-full flex-col gap-1'}>
                    {title && <div className="text-base font-medium">{title}</div>}
                    {content}
                    {description && <p className="text-text-tertiary text-sm">{description}</p>}
                  </div>
                </div>
                {action}
              </div>
              {children && <div className="tablet:px-5 px-4 pb-5">{children}</div>}
            </>
          ),
        },
        props,
      ),
    }),
)

OfferConfigurationCard.displayName = 'OfferConfigurationCard'

export { OfferConfigurationCard }
export type { OfferConfigurationCardProps }
